import { reject as _reject, some as _some, sortBy as _sortBy } from 'lodash-es';
import { useState, useMemo, useCallback } from 'react';
import Button from '@mui/material/Button';
import type { User } from '../../types/User';
import { followUserMutation, unfollowUserMutation } from '../../apollo/users';
import { meQuery } from '../../apollo/auth';
import { useAppContext } from '../../contexts/app';
import { useMutation } from '@apollo/client/react';

type Props = {
  user: User;
  [key: string]: unknown;
};

const UserFollowToggle = ({ user, ...rest }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const { login, loggedInUser, isLoggedIn } = useAppContext();

  const [followUser] = useMutation(followUserMutation, {
    update: (proxy, { data: { followUser: userFollow } }) => {
      const data = proxy.readQuery({
        query: meQuery,
      });
      proxy.writeQuery({
        query: meQuery,
        data: {
          ...data,
          me: {
            ...data.me,
            userFollows: _sortBy(
              [...data.me.userFollows, userFollow],
              ['followingUser.givenName', 'followingUser.familyName', 'followingUser.username']
            ),
          },
        },
      });
    },
  });

  const [unfollowUser] = useMutation(unfollowUserMutation, {
    update: (proxy, { data: { unfollowUser: userFollow } }) => {
      const data = proxy.readQuery({
        query: meQuery,
      });
      proxy.writeQuery({
        query: meQuery,
        data: {
          ...data,
          me: {
            ...data.me,
            userFollows: _reject(data.me.userFollows, ({ id }) => id === userFollow.id),
          },
        },
      });
    },
  });

  const following = useMemo(() => (
    loggedInUser &&
      _some(loggedInUser.userFollows, ({ followingUser: { id: followingUserId } }) => (
        followingUserId === user.id
      ))
  ), [loggedInUser, user]);

  const handleClick = useCallback((e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      login(`/people/${user.username}`);
    } else if (following) {
      setSubmitting(true);
      unfollowUser({
        variables: { id: user.id },
      }).then(() => setSubmitting(false));
    } else {
      setSubmitting(true);
      followUser({
        variables: { id: user.id },
      }).then(() => setSubmitting(false));
    }
  }, [following, isLoggedIn, user, followUser, unfollowUser, setSubmitting, login]);

  if (loggedInUser && loggedInUser.id === user.id) {
    return <div>It&apos;s you!</div>;
  }

  return (
    <Button
      variant={following ? 'outlined' : 'contained'}
      color={following ? 'inherit' : 'primary'}
      onClick={handleClick}
      disabled={submitting}
      {...rest}
    >
      {isLoggedIn && following ? 'Following' : 'Follow'}
    </Button>
  );
};

export default UserFollowToggle;
